import React, { useEffect, useState } from "react";
import { apiInstanceFetch } from "../../../util/api";
import axios from "axios";
import { setToast } from "../../../util/toast";
import Title from "../../extra/Title";

const ExchangeRateSetting = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState(null);
  const [editRate, setEditRate] = useState("");
  const [editSymbol, setEditSymbol] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Live rates state
  const [liveRates, setLiveRates] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveLastUpdated, setLiveLastUpdated] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Add currency state
  const [showAdd, setShowAdd] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addName, setAddName] = useState("");
  const [addSymbol, setAddSymbol] = useState("");
  const [addFlag, setAddFlag] = useState("");
  const [addRate, setAddRate] = useState("");
  const [adding, setAdding] = useState(false);

  // Delete state
  const [deleteCode, setDeleteCode] = useState(null);

  const fetchRates = () => {
    setLoading(true);
    apiInstanceFetch
      .get("exchangeRate/rates")
      .then((res) => {
        if (res.status && res.data) {
          setRates(res.data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleEdit = (row) => {
    setEditRow(row);
    setEditRate(row.rate.toString());
    setEditSymbol(row.symbol);
    setEditActive(row.isActive);
  };

  const handleSave = () => {
    if (!editRow) return;
    const newRate = parseFloat(editRate);
    if (isNaN(newRate) || newRate <= 0) {
      setToast("error", "Rate must be a positive number");
      return;
    }
    setSaving(true);
    axios
      .patch(
        `exchangeRate/update?currencyCode=${editRow.currencyCode}`,
        {
          rate: newRate,
          symbol: editSymbol,
          isActive: editActive,
        }
      )
      .then((res) => {
        if (res.data.status) {
          setToast("success", `${editRow.currencyCode} rate updated successfully`);
          setEditRow(null);
          fetchRates();
        } else {
          setToast("error", res.data.message || "Failed to update");
        }
      })
      .catch((err) => {
        setToast("error", "Failed to update exchange rate");
      })
      .finally(() => setSaving(false));
  };

  const handleCancel = () => {
    setEditRow(null);
  };

  // Fetch live rates comparison
  const handleFetchLive = () => {
    setLiveLoading(true);
    apiInstanceFetch
      .get("exchangeRate/live")
      .then((res) => {
        if (res.status && res.data) {
          setLiveRates(res.data);
          setLiveLastUpdated(res.lastUpdated);
        } else {
          setToast("error", res.message || "Failed to fetch live rates");
        }
      })
      .catch((err) => {
        console.error(err);
        setToast("error", "Failed to fetch live rates");
      })
      .finally(() => setLiveLoading(false));
  };

  // Sync all rates with live
  const handleSyncAll = () => {
    setSyncing(true);
    axios
      .post("exchangeRate/sync")
      .then((res) => {
        if (res.data.status) {
          setToast("success", res.data.message);
          setLiveRates(null);
          fetchRates();
        } else {
          setToast("error", res.data.message || "Failed to sync");
        }
      })
      .catch((err) => {
        console.error(err);
        setToast("error", "Failed to sync live rates");
      })
      .finally(() => setSyncing(false));
  };

  // Apply a single live rate
  const handleApplySingle = (code, liveRate) => {
    axios
      .patch(`exchangeRate/update?currencyCode=${code}`, { rate: liveRate })
      .then((res) => {
        if (res.data.status) {
          setToast("success", `${code} rate updated to ${liveRate}`);
          fetchRates();
          setLiveRates((prev) =>
            prev
              ? prev.map((r) =>
                  r.currencyCode === code
                    ? { ...r, currentRate: liveRate, diffPercent: 0 }
                    : r
                )
              : prev
          );
        } else {
          setToast("error", res.data.message || "Failed to update");
        }
      })
      .catch(() => setToast("error", "Failed to apply rate"));
  };

  // Add new currency
  const handleAddCurrency = () => {
    if (!addCode || !addName || !addSymbol) {
      setToast("error", "Currency code, name, and symbol are required");
      return;
    }
    setAdding(true);
    axios
      .post("exchangeRate/add", {
        currencyCode: addCode.toUpperCase().trim(),
        currencyName: addName.trim(),
        symbol: addSymbol.trim(),
        flag: addFlag.trim(),
        rate: addRate ? parseFloat(addRate) : undefined,
      })
      .then((res) => {
        if (res.data.status) {
          setToast("success", res.data.message);
          setShowAdd(false);
          setAddCode("");
          setAddName("");
          setAddSymbol("");
          setAddFlag("");
          setAddRate("");
          fetchRates();
        } else {
          setToast("error", res.data.message || "Failed to add currency");
        }
      })
      .catch((err) => {
        console.error(err);
        setToast("error", "Failed to add currency");
      })
      .finally(() => setAdding(false));
  };

  // Delete currency
  const handleDelete = (code) => {
    axios
      .delete(`exchangeRate/delete?currencyCode=${code}`)
      .then((res) => {
        if (res.data.status) {
          setToast("success", res.data.message);
          setDeleteCode(null);
          fetchRates();
        } else {
          setToast("error", res.data.message || "Failed to delete");
        }
      })
      .catch(() => setToast("error", "Failed to delete currency"));
  };

  return (
    <div className="mainCategory">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Title name="Exchange Rates" />
        <button
          className="btn"
          onClick={() => setShowAdd(true)}
          style={{
            background: "#10B981",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "10px",
            padding: "8px 20px",
          }}
        >
          + Add Currency
        </button>
      </div>
      <div className="mb-3">
        <small style={{ color: "#6B7280" }}>
          Manage currency exchange rates. All product prices are stored in USD
          (base currency) with 5% markup. Rates define how 1 USD converts to each target
          currency.
        </small>
      </div>

      {/* Live Rates Section */}
      <div
        style={{
          background: "#F0F9FF",
          border: "1px solid #BAE6FD",
          borderRadius: "12px",
          padding: "16px 20px",
          marginBottom: "20px",
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h6 className="fw-bold mb-0" style={{ color: "#0369A1" }}>
              Live Exchange Rates
            </h6>
            <small style={{ color: "#6B7280" }}>
              Import real-time rates from the global market to keep your prices accurate
            </small>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm"
              onClick={handleFetchLive}
              disabled={liveLoading}
              style={{
                background: "#0EA5E9",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                padding: "6px 18px",
              }}
            >
              {liveLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                  ></span>
                  Fetching...
                </>
              ) : (
                <>Fetch Live Rates</>
              )}
            </button>
            {liveRates && (
              <button
                className="btn btn-sm"
                onClick={handleSyncAll}
                disabled={syncing}
                style={{
                  background: "#10B981",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: "8px",
                  padding: "6px 18px",
                }}
              >
                {syncing ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>
                    Syncing...
                  </>
                ) : (
                  <>Sync All Rates</>
                )}
              </button>
            )}
          </div>
        </div>

        {liveLastUpdated && (
          <small style={{ color: "#6B7280" }}>
            Market data as of:{" "}
            <strong>
              {new Date(liveLastUpdated).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
          </small>
        )}

        {/* Live rates comparison table */}
        {liveRates && (
          <div className="table-responsive mt-3">
            <table
              className="table table-sm mb-0"
              style={{ background: "transparent" }}
            >
              <thead>
                <tr>
                  <th style={{ fontSize: "12px", color: "#6B7280" }}>
                    Currency
                  </th>
                  <th style={{ fontSize: "12px", color: "#6B7280" }}>
                    Your Rate
                  </th>
                  <th style={{ fontSize: "12px", color: "#6B7280" }}>
                    Live Rate
                  </th>
                  <th style={{ fontSize: "12px", color: "#6B7280" }}>
                    Difference
                  </th>
                  <th style={{ fontSize: "12px", color: "#6B7280" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {liveRates
                  .filter((r) => r.currencyCode !== "USD")
                  .map((r) => (
                    <tr key={r.currencyCode}>
                      <td className="fw-semibold">
                        {r.symbol} {r.currencyCode}
                      </td>
                      <td>{r.currentRate}</td>
                      <td className="fw-bold">
                        {r.liveRate ? r.liveRate.toFixed(6) : "N/A"}
                      </td>
                      <td>
                        {r.diffPercent !== null ? (
                          <span
                            className="badge"
                            style={{
                              background:
                                Math.abs(r.diffPercent) > 5
                                  ? "#FEE2E2"
                                  : Math.abs(r.diffPercent) > 2
                                  ? "#FEF3C7"
                                  : "#D1FAE5",
                              color:
                                Math.abs(r.diffPercent) > 5
                                  ? "#991B1B"
                                  : Math.abs(r.diffPercent) > 2
                                  ? "#92400E"
                                  : "#065F46",
                              fontSize: "12px",
                              padding: "4px 8px",
                            }}
                          >
                            {r.diffPercent > 0 ? "+" : ""}
                            {r.diffPercent}%
                          </span>
                        ) : (
                          <span style={{ color: "#9CA3AF" }}>-</span>
                        )}
                      </td>
                      <td>
                        {r.liveRate && r.diffPercent !== 0 ? (
                          <button
                            className="btn btn-sm"
                            onClick={() =>
                              handleApplySingle(r.currencyCode, r.liveRate)
                            }
                            style={{
                              background: "#F0C420",
                              color: "#000",
                              fontWeight: 600,
                              borderRadius: "6px",
                              padding: "2px 12px",
                              fontSize: "12px",
                            }}
                          >
                            Apply
                          </button>
                        ) : r.diffPercent === 0 ? (
                          <span
                            style={{
                              color: "#10B981",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Up to date
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-warning" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover primeTable text-center">
            <thead>
              <tr>
                <th className="fw-bold text-dark">No</th>
                <th className="fw-bold text-dark">Flag</th>
                <th className="fw-bold text-dark">Currency</th>
                <th className="fw-bold text-dark">Code</th>
                <th className="fw-bold text-dark">Symbol</th>
                <th className="fw-bold text-dark">Rate (1 USD =)</th>
                <th className="fw-bold text-dark">Active</th>
                <th className="fw-bold text-dark">Last Updated</th>
                <th className="fw-bold text-dark">Action</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((row, index) => (
                <tr key={row._id}>
                  <td>{index + 1}</td>
                  <td style={{ fontSize: "22px" }}>{row.flag || ""}</td>
                  <td className="fw-semibold">{row.currencyName}</td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background:
                          row.currencyCode === "USD"
                            ? "#F0C420"
                            : "#E5E7EB",
                        color:
                          row.currencyCode === "USD" ? "#000" : "#374151",
                        fontSize: "13px",
                        padding: "5px 12px",
                      }}
                    >
                      {row.currencyCode}
                    </span>
                  </td>
                  <td style={{ fontSize: "18px" }}>{row.symbol}</td>
                  <td>
                    <span className="fw-bold" style={{ fontSize: "15px" }}>
                      {row.currencyCode === "USD" ? (
                        <span style={{ color: "#6B7280" }}>1.00 (base)</span>
                      ) : (
                        row.rate
                      )}
                    </span>
                  </td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: row.isActive ? "#D1FAE5" : "#FEE2E2",
                        color: row.isActive ? "#065F46" : "#991B1B",
                        fontSize: "12px",
                        padding: "4px 10px",
                      }}
                    >
                      {row.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ fontSize: "13px", color: "#6B7280" }}>
                    {new Date(row.updatedAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td>
                    {row.currencyCode === "USD" ? (
                      <span style={{ color: "#9CA3AF", fontSize: "13px" }}>
                        Base
                      </span>
                    ) : (
                      <div className="d-flex gap-1 justify-content-center">
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "#F0C420",
                            color: "#000",
                            fontWeight: 600,
                            borderRadius: "8px",
                            padding: "4px 14px",
                          }}
                          onClick={() => handleEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "#FEE2E2",
                            color: "#991B1B",
                            fontWeight: 600,
                            borderRadius: "8px",
                            padding: "4px 14px",
                          }}
                          onClick={() => setDeleteCode(row.currencyCode)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      {editRow && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "16px" }}>
              <div
                className="modal-header"
                style={{ borderBottom: "none", paddingBottom: 0 }}
              >
                <h5 className="modal-title fw-bold">
                  Edit {editRow.currencyName} ({editRow.currencyCode})
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Exchange Rate
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">1 USD =</span>
                    <input
                      type="number"
                      step="any"
                      className="form-control"
                      value={editRate}
                      onChange={(e) => setEditRate(e.target.value)}
                      style={{ fontSize: "18px", fontWeight: 700 }}
                    />
                    <span className="input-group-text">
                      {editRow.currencyCode}
                    </span>
                  </div>
                  <small className="text-muted">
                    Example: If 1 USD = 10.95 GHS, enter 10.95
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Symbol</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editSymbol}
                    onChange={(e) => setEditSymbol(e.target.value)}
                    style={{ fontSize: "16px" }}
                  />
                </div>
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={editActive}
                      onChange={(e) => setEditActive(e.target.checked)}
                      style={{ width: "40px", height: "20px" }}
                    />
                    <label className="form-check-label fw-semibold ms-2">
                      {editActive ? "Active" : "Inactive"}
                    </label>
                  </div>
                  <small className="text-muted">
                    Inactive currencies won't appear in the mobile app
                  </small>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", paddingTop: 0 }}
              >
                <button
                  className="btn btn-light"
                  onClick={handleCancel}
                  style={{ borderRadius: "8px", padding: "8px 24px" }}
                >
                  Cancel
                </button>
                <button
                  className="btn"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    background: "#F0C420",
                    color: "#000",
                    fontWeight: 600,
                    borderRadius: "8px",
                    padding: "8px 24px",
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Currency Dialog */}
      {showAdd && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "16px" }}>
              <div
                className="modal-header"
                style={{ borderBottom: "none", paddingBottom: 0 }}
              >
                <h5 className="modal-title fw-bold">Add New Currency</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAdd(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Currency Code *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. ZAR"
                    value={addCode}
                    onChange={(e) =>
                      setAddCode(e.target.value.toUpperCase().slice(0, 3))
                    }
                    maxLength={3}
                    style={{ fontSize: "16px", fontWeight: 700 }}
                  />
                  <small className="text-muted">
                    3-letter ISO currency code (e.g. ZAR, INR, KES)
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Currency Name *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. South African Rand"
                    value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    style={{ fontSize: "16px" }}
                  />
                </div>
                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label fw-semibold">Symbol *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. R"
                      value={addSymbol}
                      onChange={(e) => setAddSymbol(e.target.value)}
                      style={{ fontSize: "18px" }}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label fw-semibold">Flag Emoji</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 🇿🇦"
                      value={addFlag}
                      onChange={(e) => setAddFlag(e.target.value)}
                      style={{ fontSize: "18px" }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Exchange Rate
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">1 USD =</span>
                    <input
                      type="number"
                      step="any"
                      className="form-control"
                      placeholder="Auto-fetch if empty"
                      value={addRate}
                      onChange={(e) => setAddRate(e.target.value)}
                      style={{ fontSize: "16px" }}
                    />
                    <span className="input-group-text">
                      {addCode || "???"}
                    </span>
                  </div>
                  <small className="text-muted">
                    Leave empty to auto-fetch the live rate from the market
                  </small>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", paddingTop: 0 }}
              >
                <button
                  className="btn btn-light"
                  onClick={() => setShowAdd(false)}
                  style={{ borderRadius: "8px", padding: "8px 24px" }}
                >
                  Cancel
                </button>
                <button
                  className="btn"
                  onClick={handleAddCurrency}
                  disabled={adding}
                  style={{
                    background: "#10B981",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "8px",
                    padding: "8px 24px",
                  }}
                >
                  {adding ? "Adding..." : "Add Currency"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteCode && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content" style={{ borderRadius: "16px" }}>
              <div className="modal-body text-center py-4">
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    background: "#FEE2E2",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    fontSize: "24px",
                  }}
                >
                  !
                </div>
                <h6 className="fw-bold mb-2">Delete {deleteCode}?</h6>
                <p style={{ color: "#6B7280", fontSize: "14px" }}>
                  This currency will be removed from the app. Users who selected
                  it will be switched to USD.
                </p>
                <div className="d-flex gap-2 justify-content-center mt-3">
                  <button
                    className="btn btn-light"
                    onClick={() => setDeleteCode(null)}
                    style={{ borderRadius: "8px", padding: "8px 24px" }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn"
                    onClick={() => handleDelete(deleteCode)}
                    style={{
                      background: "#EF4444",
                      color: "#fff",
                      fontWeight: 600,
                      borderRadius: "8px",
                      padding: "8px 24px",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeRateSetting;
